import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { PHONE_TEL, scrollToSection } from "@/lib/site";

const LINKS = [
  { label: "Services", id: "services", testid: "nav-link-services" },
  { label: "Pricing", id: "pricing", testid: "nav-link-pricing" },
  { label: "Why Us", id: "why-us", testid: "nav-link-why-us" },
  { label: "Process", id: "process", testid: "nav-link-process" },
  { label: "Gallery", id: "gallery", testid: "nav-link-gallery" },
  { label: "FAQ", id: "faq", testid: "nav-link-faq" },
];

export const Navbar = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenuOpen(false);
    scrollToSection(id);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || menuOpen
          ? "border-b border-white/10 bg-black/70 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 md:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="flex items-center gap-3"
        >
          <img
            src="/assets/logo-emblem.png"
            alt="Syed's Pro Drive Inspection logo"
            className="h-10 w-10 rounded-xl border border-gold/30 object-cover"
          />
          <span className="text-left leading-none">
            <span className="block font-heading text-sm font-extrabold tracking-tight text-white">
              Syed's Pro Drive
            </span>
            <span className="block text-[10px] font-bold uppercase tracking-[0.28em] text-gold">
              Inspection
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="text-sm font-semibold text-white/60 transition-colors duration-300 hover:text-gold"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-call-btn"
            href={PHONE_TEL}
            className="hidden items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white/80 transition-[border-color,color] duration-300 hover:border-gold/50 hover:text-gold md:flex"
          >
            <Phone className="h-4 w-4" />
            Call Now
          </a>
          <button
            data-testid="nav-book-inspection-btn"
            onClick={onBook}
            className="rounded-full bg-gold px-5 py-2.5 font-heading text-sm font-extrabold text-black transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold-light"
          >
            Book Inspection
          </button>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white lg:hidden"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          data-testid="nav-mobile-menu"
          className="border-t border-white/10 bg-black/90 px-5 py-6 backdrop-blur-xl lg:hidden"
        >
          <div className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-${l.testid}`}
                onClick={() => go(l.id)}
                className="rounded-xl px-4 py-3 text-left font-heading text-lg font-bold text-white/80 transition-colors duration-300 hover:bg-white/5 hover:text-gold"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
